import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Polyline, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Package, Truck, CircleDot, Timer, Check, Loader2, ShieldAlert, Star } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

// Public tracking page for a delivery. Requires the share_token issued at creation.
// No auth cookie is sent — this uses `axios` directly (not the credentialed `api` client)
// so an unauthenticated recipient can view without triggering CORS auth flows.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconFor = (color) => L.divIcon({
  className: "",
  html: `<div style="width:20px;height:20px;border-radius:9999px;background:${color};border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>`,
  iconAnchor: [10, 10],
});
const pickupIcon = iconFor("#059669");
const dropIcon = iconFor("#dc2626");
const agentIcon = iconFor("#E05E36");

const STATUS_META = {
  requested: { label: "Requested", color: "bg-blue-100 text-blue-800" },
  assigned: { label: "Assigned", color: "bg-amber-100 text-amber-800" },
  picked_up: { label: "Picked up", color: "bg-fuchsia-100 text-fuchsia-800" },
  in_transit: { label: "In transit", color: "bg-orange-100 text-orange-800" },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Cancelled", color: "bg-stone-200 text-stone-700" },
};

const relTime = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function TrackDelivery() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const token = search.get("token") || "";
  const [doc, setDoc] = useState(null);
  const [trail, setTrail] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!id || !token) { setError("Missing tracking link parameters."); return; }
    try {
      const [d, t] = await Promise.all([
        axios.get(`${API}/delivery/${id}/public`, { params: { token } }),
        axios.get(`${API}/delivery/${id}/public/trail`, { params: { token } }).catch(() => ({ data: { pings: [] } })),
      ]);
      setDoc(d.data);
      setTrail(t.data.pings || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Tracking unavailable");
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Poll every 8s while delivery is active (delivered/cancelled → stop polling).
    if (!doc || ["delivered", "cancelled"].includes(doc.status)) return;
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [doc, load]);

  if (error) return (
    <div className="max-w-md mx-auto px-4 py-12 text-center" data-testid="track-delivery-error">
      <ShieldAlert className="mx-auto text-red-500 mb-2" size={40} />
      <div className="font-bold text-stone-900">Can't open this tracker</div>
      <div className="text-sm text-stone-500 mt-1">{error}</div>
    </div>
  );
  if (!doc) return <div className="p-12 text-center"><Loader2 className="inline animate-spin" /></div>;

  const center = doc.agent_current
    ? [doc.agent_current.lat, doc.agent_current.lng]
    : [(doc.pickup.lat + doc.dropoff.lat) / 2, (doc.pickup.lng + doc.dropoff.lng) / 2];
  const polyline = trail.length > 1
    ? trail.map(p => [p.lat, p.lng])
    : [[doc.pickup.lat, doc.pickup.lng], [doc.dropoff.lat, doc.dropoff.lng]];
  const st = STATUS_META[doc.status] || STATUS_META.requested;

  return (
    <div className="max-w-3xl mx-auto px-4 py-4" data-testid="track-delivery-page">
      <div className="flex items-center gap-2 mb-3">
        <Truck className="text-[#E05E36]" size={22} />
        <h1 className="heading text-xl font-black text-stone-900">Tracking your delivery</h1>
      </div>

      <div className="bg-white border border-stone-200 rounded-3xl overflow-hidden">
        <div style={{ height: 340 }}>
          <MapContainer center={center} zoom={13} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
            <Marker position={[doc.pickup.lat, doc.pickup.lng]} icon={pickupIcon}><Popup>Pickup</Popup></Marker>
            <Marker position={[doc.dropoff.lat, doc.dropoff.lng]} icon={dropIcon}><Popup>Drop-off</Popup></Marker>
            {doc.agent_current && (
              <Marker position={[doc.agent_current.lat, doc.agent_current.lng]} icon={agentIcon}>
                <Popup>{doc.agent_name || "Agent"} · {relTime(doc.agent_current.at)}</Popup>
              </Marker>
            )}
            <Polyline positions={polyline} color="#E05E36" weight={3} dashArray="6" />
          </MapContainer>
        </div>

        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] text-stone-500 font-mono" data-testid="track-delivery-id">{doc.delivery_id}</div>
              <div className="font-bold text-sm">From {doc.sender_name} → {doc.recipient_name}</div>
            </div>
            <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${st.color}`} data-testid="track-delivery-status">{st.label}</span>
          </div>

          <div className="text-xs text-stone-600 space-y-0.5">
            <div><CircleDot size={10} className="inline mr-1 text-emerald-600" /> {doc.pickup?.address}</div>
            <div><CircleDot size={10} className="inline mr-1 text-red-600" /> {doc.dropoff?.address}</div>
            <div><Timer size={10} className="inline mr-1" /> {doc.distance_km} km · ETA {doc.eta_minutes} min</div>
            {doc.package_desc && <div><Package size={10} className="inline mr-1" /> {doc.package_desc}</div>}
          </div>

          {doc.agent_current && (
            <div className="text-xs bg-orange-50 border border-orange-200 rounded-lg p-2 text-orange-900">
              Courier last seen {relTime(doc.agent_current.at)}
              {doc.agent_current.speed_kmh ? ` · ${doc.agent_current.speed_kmh.toFixed(0)} km/h` : ""}
            </div>
          )}

          {/* Rate the delivery — appears the moment status flips to `delivered`.
              Token-guarded server-side, one-shot. Recipients don't need to sign in. */}
          {doc.status === "delivered" && (
            <RateDeliveryBlock
              deliveryId={doc.delivery_id}
              token={token}
              existing={doc.public_rating}
              onRated={(r) => setDoc(d => ({ ...d, public_rating: { rating: r.rating, at: new Date().toISOString() } }))}
            />
          )}

          <div className="border-t border-stone-100 pt-3">
            <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-1">Timeline</div>
            <ul className="text-xs space-y-0.5" data-testid="track-delivery-timeline">
              {(doc.status_timeline || []).map((t, i) => (
                <li key={i} className="flex items-center gap-2 text-stone-600">
                  <Check size={11} className="text-emerald-500" />
                  <span className="font-bold">{STATUS_META[t.status]?.label || t.status}</span>
                  <span className="text-stone-400">· {relTime(t.at)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="text-[11px] text-stone-400 text-center mt-3">
        Powered by Find2Near · This tracking link auto-refreshes every 8 seconds.
      </div>
    </div>
  );
}


// Star rating widget for a delivered parcel — POST-once, token-guarded.
function RateDeliveryBlock({ deliveryId, token, existing, onRated }) {
  const [hover, setHover] = useState(0);
  const [picked, setPicked] = useState(existing?.rating || 0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  if (existing?.rating) {
    return (
      <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4" data-testid="delivery-rated">
        <div className="text-[10px] uppercase tracking-widest font-black text-emerald-700">You've rated this delivery</div>
        <div className="mt-1 flex items-center gap-1">
          {[1, 2, 3, 4, 5].map(n => (
            <Star key={n} size={20} className={n <= existing.rating ? "fill-[#DDA77B] text-[#DDA77B]" : "text-stone-300"} />
          ))}
          <span className="ml-2 font-bold text-stone-900">{existing.rating}/5</span>
        </div>
        {existing.comment && <div className="mt-1.5 text-sm text-stone-700 italic">"{existing.comment}"</div>}
      </div>
    );
  }

  const submit = async () => {
    if (picked < 1) { toast.error("Pick a rating first"); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/delivery/${deliveryId}/public/rate`,
        { rating: picked, comment: comment.trim() || null },
        { params: { token } },
      );
      toast.success("Thanks — rating submitted!");
      onRated?.({ rating: picked });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit rating");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 space-y-3" data-testid="delivery-rate-widget">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-black text-emerald-700">How was your delivery?</div>
        <div className="text-sm text-stone-700 mt-0.5">Rate the courier — no signup needed. Helps the next customer.</div>
      </div>
      <div
        className="flex items-center gap-1"
        onMouseLeave={() => setHover(0)}
        data-testid="delivery-rate-stars"
      >
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            type="button"
            onMouseEnter={() => setHover(n)}
            onClick={() => setPicked(n)}
            className="p-1"
            data-testid={`delivery-rate-star-${n}`}
            aria-label={`Rate ${n} out of 5`}
          >
            <Star
              size={30}
              className={n <= (hover || picked) ? "fill-[#DDA77B] text-[#DDA77B]" : "text-stone-300"}
            />
          </button>
        ))}
        {picked > 0 && <span className="ml-2 font-bold text-stone-900">{picked}/5</span>}
      </div>
      <textarea
        value={comment}
        onChange={e => setComment(e.target.value.slice(0, 500))}
        placeholder="Anything to add? (optional)"
        className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm outline-none focus:border-emerald-500 resize-none"
        rows={2}
        data-testid="delivery-rate-comment"
      />
      <button
        onClick={submit}
        disabled={saving || picked < 1}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold py-2.5 disabled:opacity-50"
        data-testid="delivery-rate-submit"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Star size={14} />}
        {saving ? "Sending…" : "Submit rating"}
      </button>
    </div>
  );
}
